if (!customElements.get('newsletter-form')) {
  class NewsletterForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.input = this.form.querySelector('input[type="email"]');
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.messageEl = this.querySelector('.js-form-message');

      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Handles submission of the newsletter form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      this.setMessage(false);

      // Let the browser surface its own validation UI for a malformed address.
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        this.setMessage(this.dataset.errorMessage, 'error');
        return;
      }

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      try {
        // The customer form_type has no JSON endpoint, so this posts the form
        // normally and inspects where Shopify redirected to. On success it
        // appends customer_posted=true; on failure it re-renders in place.
        const response = await fetch(this.form.action.split('#')[0], {
          method: 'POST',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new FormData(this.form)
        });

        if (!response.ok) throw new Error(response.status);

        const html = await response.text();

        if (NewsletterForm.isSuccess(response, html)) {
          this.setMessage(this.dataset.successMessage, 'success');
          this.form.reset();

          this.dispatchEvent(new CustomEvent('on:newsletter:success', {
            bubbles: true,
            detail: { email: this.input.value }
          }));
        } else {
          this.setMessage(NewsletterForm.parseError(html) || this.dataset.errorMessage, 'error');

          this.dispatchEvent(new CustomEvent('on:newsletter:error', {
            bubbles: true,
            detail: { error: this.messageEl?.textContent }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.setMessage(this.dataset.errorMessage, 'error');

        this.dispatchEvent(new CustomEvent('on:newsletter:error', {
          bubbles: true,
          detail: { error }
        }));
      } finally {
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.classList.remove('is-loading');
      }
    }

    /**
     * Determines whether the submission was accepted.
     *
     * Shopify redirects to return_to on success (which carries
     * customer_posted=true) and re-renders the form with errors on failure.
     * @param {object} response - Fetch response.
     * @param {string} html - Response HTML.
     * @returns {boolean}
     */
    static isSuccess(response, html) {
      try {
        if (new URL(response.url).searchParams.get('customer_posted') === 'true') return true;
      } catch (error) {
        // response.url unavailable - fall through to inspecting the body.
      }

      return html.includes('customer_posted=true') && !NewsletterForm.parseError(html);
    }

    /**
     * Pulls a validation message out of a re-rendered response page.
     * @param {string} html - Response HTML.
     * @returns {?string}
     */
    static parseError(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const errorEl = doc.querySelector('.form__field-error, .errors, .form-status--error');
      const text = errorEl?.textContent.trim();
      return text || null;
    }

    /**
     * Shows or clears the form message.
     * @param {string|boolean} text - Message to show, or false to clear.
     * @param {string} [state] - Either 'success' or 'error'.
     */
    setMessage(text, state) {
      if (!this.messageEl) return;

      this.messageEl.hidden = !text;
      this.messageEl.classList.remove('form-message--success', 'form-message--error');

      if (!text) {
        this.messageEl.textContent = '';
        return;
      }

      this.messageEl.textContent = text;
      this.messageEl.classList.add(`form-message--${state}`);
    }
  }

  customElements.define('newsletter-form', NewsletterForm);
}
